import {
    ActionMenuBadge,
    AuthContext,
    BadgeLabeledValue,
    ClaimCloseoutType,
    ClaimDetailsPanel,
    ClaimOverview,
    ClaimPayerSummary,
    ClaimResponsibilityType,
    ClaimType,
    CollectionPaymentSourceType,
    FacilityBadge,
    formatNumber,
    formatDate,
    ClaimResponsibilityCategoryBadge,
    getResponsibilityDescription,
    getClaimPriorAuthorizations,
    IActionMenuBadgeItem,
    IAuthContextProps,
    InsuranceOverview,
    Invoice,
    InvoiceType,
    IToastContextProps,
    LoadingMeter,
    MissingIdBadge,
    parseAmount,
    PersonalOverview,
    PriorAuthBadge,
    ProviderDetail,
    getProviderTypeLabel,
    ProviderBadge,
    ProviderChargemasterItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderCoding,
    ProviderCollectionPayment,
    ProviderComment,
    ProviderDocument,
    ProviderFacility,
    ProviderGuarantor,
    ProviderInstitutionalClaim,
    ProviderInstitutionalClaimTemplate,
    ProviderLetter,
    ProviderMail,
    ProviderMessage,
    ProviderNynf3Claim,
    ProviderNynf3ClaimTemplate,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderPaymentPlan,
    ProviderPhone,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    ProviderReceiveFax,
    ProviderSeller,
    ProviderSendEmail,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    RecordOfServices,
    SellerBadge,
    SupportCheckoutCharge,
    ProviderCollectionStatusType,
    SupportCreditCard,
    SupportHL7Message,
    ToastContext,
    Tooltip,
    Transaction,
    truncateUuid,
    renderString,
    calculateClaimFinancialsFromProfessionalServices,
    calculateClaimFinancialsFromInstitutionalServices,
    getEmptyClaimFinancials,
    ClaimProgressTracker,
    ClaimFinancialsSection,
    ClaimAiSummaryCard,
    ClaimCoderRuleSuppression,
    toClaimFinancialSummary,
    normalizeStr,
    buildHl7ClaimOverview,
    ClaimUpdatingNotice,
    fetchPayersByIds,
    getHl7PayerIds,
    IPendingHl7Snapshot,
    resolvePendingHl7Snapshot,
} from '@enter/core';
import { resolveHasAdditionalCoverage } from '@enter/core/src/ui/claim-progress-tracker/claim-progress-derivation';
import { getCodingOutcomeSignature } from '@enter/core/src/ui/claims/coding-signature.util';
import {
    mdiPlus,
    mdiAlertCircle,
    mdiPause,
    mdiCheck,
    mdiAccountOutline,
    mdiPencilOutline,
    mdiClipboardTextOutline,
    mdiOpenInNew,
    mdiCreationOutline,
} from '@mdi/js';
import { AiAssistant, ExtensionUnavailableModal, useTalkToAiTrigger } from '@enter/core/src/ui/ai-assistant';
import {
    AttachedItem,
    ClaimContextSources,
    buildClaimAttachableEntities,
    buildClaimSmartContext,
} from '@enter/core/src/services/ai-adapter/smart-context';
import { BadgeIcon } from '@enter/core/src/ui/badge/badge-icon';
import { Fade } from '@mui/material';
import { IProviderMessagingContextProps, PageLayout, ProviderMessagingContext } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, ErrorDialog, IDialogContextProps } from 'services';
import { withRouter, RouterProps } from 'router/with-router';
import { parseError } from 'services/dialog/util';
import {
    ClaimDetailsKey,
    ClaimStore,
    IClaimDetail,
    IInjectedRootStore,
    IObservedRootStore,
    MessageStore,
    RootStore,
} from 'stores';
import { ClaimService } from 'stores/claim.service';
import { ICms1500Selection, showCms1500Dialog } from './components/cms1500-dialog/cms1500-dialog';
import { ICms1500Choice } from './components/claim-timeline/resource-mapper';
import { coderRuleSuppressionApi } from 'stores/coder-rule-suppression.service';
import * as S from './claim-detail.styles';
import { BillingTimelineTable, GuarantorHeader } from './components';
import { showRecordDiscountDialog } from './components/show-record-discount-dialog';
import { CollectionPaymentDialog } from './components/collection-payment-dialog';
import { aggregateClaimResources, getProviderItem } from './components/claim-timeline';
import { ClaimTimeline } from '@enter/core';
import { CollectionRefundDialog } from './components/collection-refund-dialog';
import { MaxProviderPRDialog } from './components/maxpr-provider-dialog';
import { ProviderCloseoutDialog } from './closeout-dialog/closeout-dialog';
import { ClaimBundle } from 'components/badges/claim-bundle';
import qs from 'qs';
import Icon from '@mdi/react';
const formatAmount = formatNumber('Currency');

interface IMatchParams {
    claimId: string;
    accountId?: string;
}

interface IClaimDetailProps extends RouterProps<IMatchParams>, IInjectedRootStore {
    authContext: IAuthContextProps;
    messagingContext: IProviderMessagingContextProps;
    dialogContext: IDialogContextProps;
    toastContext: IToastContextProps;
}

interface IClaimDetailState {
    claim: ProviderClaim | undefined;
    claimDetail: IClaimDetail | undefined;
    claimBundle: ProviderClaim[];
    loading: boolean;
    loadingBundle: boolean;
    showAlert: boolean;
    legacy?: string;
    aiOpen: boolean;
    /** Payers named by a pending HL7 payload, fetched after the page renders. */
    hl7Payers: ProviderPayer[];
    hl7PayersLoading: boolean;
}

@observer
class ClaimDetailPage extends React.Component<IClaimDetailProps, IClaimDetailState> {
    private claimId: string;
    // Identifies the current `fetchData` run. `claimId` alone cannot do it: navigating A → B → A
    // brings the id back while the first A request is still in flight, and comparing ids would let
    // that response through as if it were the current one.
    private fetchToken = 0;
    constructor(props: IClaimDetailProps) {
        super(props);
        this.claimId = this.props.match.params.claimId;
        if (this.claimId === undefined) {
            throw Error('No such claim');
        }

        const parseQueryParams = <T extends {}>(location: Location, initial: T): T => {
            const params = location.search.substring(1);
            const parsed = qs.parse(params);
            const picked = _.pick(parsed, _.keys(initial));
            return _.defaults(picked, initial);
        };

        const claimDetailsParams = parseQueryParams(location, {
            legacy: '',
        });

        this.state = {
            claim: undefined,
            claimDetail: undefined,
            claimBundle: [],
            loading: false,
            loadingBundle: false,
            showAlert: false,
            legacy: claimDetailsParams.legacy,
            aiOpen: false,
            hl7Payers: [],
            hl7PayersLoading: false,
        };
    }

    private buildClaimAiContextSources(): ClaimContextSources {
        const { claim, claimDetail } = this.state;
        return {
            claim: claimDetail?.claim ?? claim,
            professionals: claimDetail?.professionalClaim ? [claimDetail.professionalClaim] : undefined,
            hl7s: claimDetail?.hl7Messages,
            eligibilities: claimDetail?.eligibilities,
            codings: claimDetail?.codings,
            messages: this.messages,
            payments: claimDetail?.payments,
            collectionPayments: claimDetail?.collections,
            transmissions: claimDetail?.transmissions,
            invoices: claimDetail?.invoices,
            documents: claimDetail?.documents,
            letters: claimDetail?.letters,
            receiveFaxes: claimDetail?.receiveFaxes,
            sendFaxes: claimDetail?.sendFaxes,
            history: this.props.rootStore.claimProgressStore.data,
        };
    }

    private readonly openAiAssistant = (): void => {
        this.setState({ aiOpen: true });
    };
    public componentDidMount(): void {
        window.scrollTo(0, 0);
        this.authContext.setGroup('claim-detail');
        this.fetchData();
        this.messagingContext.closePanel?.();
    }
    public componentDidUpdate(): void {
        const claimId = this.props.match.params.claimId;
        if (claimId !== this.claimId) {
            this.claimId = claimId;
            // Retire the run for the previous claim here, not when the next one starts: the restart
            // below is deferred to a setState callback, and a response arriving in between would
            // still look current.
            this.fetchToken += 1;
            // Drop what the claim being left still has in flight. Best effort only — the fetch group
            // is global and another mounted component may have moved it — so `fetchData` also
            // discards a late response on its own rather than trusting this to have caught it.
            this.authContext.cancelGroup('claim-detail');
            this.authContext.setGroup('claim-detail');
            // The route reuses this instance across claims, and `fetchData` returns early while a
            // claim is already loaded — so without clearing it the page would keep serving the
            // previous claim's data under the new id. Clearing also drops render to the loader,
            // which unmounts children holding per-claim state of their own.
            this.setState(
                { claim: undefined, claimDetail: undefined, claimBundle: [], hl7Payers: [], hl7PayersLoading: false },
                () => {
                    void this.fetchData();
                },
            );
        }
    }
    public componentWillUnmount(): void {
        // Same reason as on navigation: a response outliving the page must not write state.
        this.fetchToken += 1;
        this.authContext.cancelGroup('claim-detail');
    }
    public render(): React.ReactElement {
        return this.state.claim ? this.renderPage() : this.renderLoader();
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }
    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }
    private get messagingContext(): IProviderMessagingContextProps {
        return this.props.messagingContext;
    }
    private get claimStore(): ClaimStore {
        return this.props.rootStore.claimStore;
    }
    private get messageStore(): MessageStore {
        return this.props.rootStore.messageStore;
    }

    private get templateLoaded(): boolean {
        return (
            this.isLoaded(['professionalTemplate']) ||
            this.isLoaded(['institutionalTemplate']) ||
            this.isLoaded(['nynf3Template'])
        );
    }
    // get additional claim details for Professional/Institutional/Nynf3/Dental types
    private get claimPIND(): ProviderProfessionalClaim | ProviderInstitutionalClaim | ProviderNynf3Claim | undefined {
        if (this.state.claim) {
            switch (this.state.claim.type) {
                case ClaimType.PROFESSIONAL:
                    return this.claimStore.findProfessionalClaim(this.claimId);
                case ClaimType.INSTITUTIONAL:
                    return this.claimStore.findInstitutionalClaim(this.claimId);
                case ClaimType.NYNF3:
                    return this.claimStore.findNynf3Claim(this.claimId);
                default:
                    throw new Error(`Unsupported Claim type: ${this.state.claim.type}`);
            }
        }
        return undefined;
    }
    private get collections(): ProviderCollectionPayment[] | undefined {
        return this.claimStore.findCollections(this.state.claim);
    }
    private get payments(): ProviderClaimPayment[] | undefined {
        return this.claimStore.findPayments(this.state.claim);
    }
    private get payers(): ProviderPayer[] | undefined {
        return _.compact(_.map(this.payerSubscribers, 'payer'));
    }
    private get providers(): ProviderProvider[] | undefined {
        return this.claimStore.findProviders(this.claimId);
    }
    private get subscribers(): ProviderSubscriber[] | undefined {
        return _.compact(_.map(this.payerSubscribers, 'subscriber'));
    }

    private get payerSubscribers(): ProviderPayerSubscriber[] | undefined {
        return this.claimStore.findPayerSubscribersByClaim(this.claimId);
    }
    private get subscriberPatients(): ProviderSubscriberPatient[] | undefined {
        return this.claimStore.findSubscriberPatientsByClaim(this.claimId);
    }
    private get statuses(): ProviderStatus[] | undefined {
        return this.claimStore.findStatuses(this.claimId);
    }
    private get transmissions(): ProviderStatus[] | undefined {
        return this.claimStore.findTransmissions(this.claimId);
    }
    private get documents(): ProviderDocument[] | undefined {
        return this.claimStore.findDocuments(this.claimId);
    }
    private get eligibilities(): ProviderClaimEligibility[] | undefined {
        return this.claimStore.findEligibilities(this.claimId);
    }
    private get facility(): ProviderFacility | undefined {
        return _.first(this.claimStore.findFacilities(this.claimId));
    }
    private get guarantor(): ProviderGuarantor | undefined {
        return _.first(this.claimStore.findGuarantors(this.claimId));
    }
    private get hl7Messages(): SupportHL7Message[] | undefined {
        return this.claimStore.findHL7Messages(this.claimId);
    }
    /**
     * Newer claim data from HL7 that the displayed professional record has not caught up with. While set, the
     * overview reads from it; the page returns to the professional record on the first load after it is newer.
     */
    private get pendingHl7Snapshot(): IPendingHl7Snapshot | undefined {
        return this.resolvePendingHl7(this.state.claimDetail);
    }
    private resolvePendingHl7(claimDetail: IClaimDetail | undefined): IPendingHl7Snapshot | undefined {
        if (!claimDetail || claimDetail.claim?.type !== ClaimType.PROFESSIONAL) {
            return undefined;
        }
        return resolvePendingHl7Snapshot(claimDetail.hl7Messages, claimDetail.professionalClaim);
    }
    private get checkoutCharges(): SupportCheckoutCharge[] | undefined {
        return this.claimStore.findCheckoutCharges(this.guarantor);
    }
    private get creditCards(): SupportCreditCard[] | undefined {
        return this.claimStore.findCreditCards(this.guarantor);
    }
    private get paymentPlans(): ProviderPaymentPlan[] | undefined {
        return this.claimStore.findPaymentPlansByGuarantorId(_.get(this.guarantor, 'id', ''));
    }
    private get codings(): ProviderCoding[] | undefined {
        return this.claimStore.findCodings(this.claimId);
    }
    private get comments(): ProviderComment[] | undefined {
        return this.claimStore.findComments(this.claimId);
    }
    private get invoices(): Invoice[] | undefined {
        return this.claimStore.findInvoices(this.claimId);
    }
    private get institutionalTemplate(): ProviderInstitutionalClaimTemplate | undefined {
        return this.claimStore.findInstitutionalTemplate(this.claimId);
    }
    private get nynf3Template(): ProviderNynf3ClaimTemplate | undefined {
        return this.claimStore.findNynf3Template(this.claimId);
    }
    private get professionalTemplate(): ProviderProfessionalClaimTemplate | undefined {
        return this.claimStore.findProfessionalTemplate(this.claimId);
    }
    private get template():
        | ProviderProfessionalClaimTemplate
        | ProviderInstitutionalClaimTemplate
        | ProviderNynf3ClaimTemplate
        | undefined {
        return _.first(_.compact([this.institutionalTemplate, this.nynf3Template, this.professionalTemplate]));
    }
    private get transactions(): Transaction[] | undefined {
        return this.claimStore.findTransactions(this.claimId);
    }
    private get letters(): ProviderLetter[] | undefined {
        return this.claimStore.findLetters(this.claimId);
    }
    private get mails(): ProviderMail[] | undefined {
        return this.claimStore.findMails(this.claimId);
    }
    private get phones(): ProviderPhone[] | undefined {
        return this.claimStore.findPhones(this.claimId);
    }
    private get receiveFaxes(): ProviderReceiveFax[] | undefined {
        return this.claimStore.findReceiveFaxes(this.claimId);
    }
    private get sendFaxes(): ProviderSendFax[] | undefined {
        return this.claimStore.findSendFaxes(this.claimId);
    }
    private get emails(): ProviderSendEmail[] | undefined {
        return this.claimStore.findEmails(this.claimId);
    }
    private get seller(): ProviderSeller | undefined {
        return _.first(this.claimStore.findSeller(this.claimId));
    }
    private get chargemasterItems(): ProviderChargemasterItem[] | undefined {
        return this.claimStore.findChargemasterItems(this.claimId);
    }
    private get messages(): ProviderMessage[] | undefined {
        // Filter messages that are linked to this specific claim
        const allMessages = this.messageStore.findMessages({});
        return _.filter(allMessages, message => _.some(message.links?.claims, claim => claim.id === this.claimId));
    }

    private readonly renderClaimHaltedBanner = (): React.ReactElement | undefined => {
        const HALTED_CATEGORY_NAME = 'Claim Halted';

        const haltedCategoryId = _.find(this.messageStore.messageCategories, { name: HALTED_CATEGORY_NAME })?.id;
        if (!haltedCategoryId) {
            return;
        }
        const haltedMessage = _.find(this.messages || [], message => {
            const matchesHaltedCategory = _.some(
                message.links?.messageCategories || [],
                category => category.id === haltedCategoryId,
            );

            if (!matchesHaltedCategory) return false;

            const hasCommentsArray = Array.isArray(message.comments);
            const commentsCount = hasCommentsArray ? message.comments!.length : undefined;
            const fallbackCommentCount = typeof message.commentCount === 'number' ? message.commentCount : undefined;

            if (typeof commentsCount === 'number') return commentsCount === 1;
            if (typeof fallbackCommentCount === 'number') return fallbackCommentCount === 1;

            return false;
        });

        if (!haltedMessage?.id) {
            return;
        }

        const handleViewTask = (): void => {
            this.messagingContext.openPanel?.({
                claimId: this.claimId,
                messageId: haltedMessage.id,
            });
        };

        return (
            <S.Alert>
                <S.HaltedAlertContainer>
                    <Icon path={mdiPause} size={1} />
                    <S.AlertTitle>Claim Halted</S.AlertTitle>
                    <S.AlertDescription>This claim is halted and needs to be resumed manually.</S.AlertDescription>
                </S.HaltedAlertContainer>
                <S.AlertButton variant="primary" size="small" onClick={handleViewTask}>
                    View task
                </S.AlertButton>
            </S.Alert>
        );
    };

    // Mirrors the Record of Services summary row (shared calculation in @enter/core)
    private readonly calculateClaimFinancials = () => {
        const template = this.template;
        const payments = this.payments || [];
        const chargemasterItems = this.chargemasterItems || [];
        const patientPaid = this.state.claim?.patientInvoicePaidAmount;

        if (template) {
            // Professional template
            if ('f24ServiceLine' in template) {
                const serviceLines = (template as ProviderProfessionalClaimTemplate).f24ServiceLine || [];
                return calculateClaimFinancialsFromProfessionalServices(
                    serviceLines,
                    payments,
                    patientPaid,
                    chargemasterItems,
                );
            }
            // Institutional template
            if ('f42ClaimLines' in template) {
                const serviceLines = (template as ProviderInstitutionalClaimTemplate).f42ClaimLines || [];
                return calculateClaimFinancialsFromInstitutionalServices(
                    serviceLines,
                    payments,
                    patientPaid,
                    chargemasterItems,
                );
            }
        }

        return getEmptyClaimFinancials();
    };

    private get openedMessages(): ProviderMessage[] {
        return _.filter(this.messages, m => !!m.open);
    }

    private fetchMessages = (claimId: string) => {
        // Deliberately not awaited — messages fill in beside the claim rather than gating it, so
        // nothing downstream is in a position to handle a rejection. Leaving a claim aborts these
        // requests, which makes an AbortError the ordinary path here rather than a fault to report.
        void this.messageStore.loadMessagesForClaim(claimId).catch(() => undefined);
        // Load message categories when loading claim details
        void this.messageStore.loadMessageCategories().catch(() => undefined);
        if (!_.isEmpty(this.openedMessages)) {
            this.setState({
                showAlert: true,
            });
        }
    };

    private fetchClaimDetails = async (claimId: string) => {
        return this.claimStore.loadClaimDetail(claimId, this.state.legacy === 'true');
        // if(this.state.legacy === 'true') {
        //     return this.claimStore.loadClaimDetail(this.claimId);
        // } else {
        //     const claimDetails: IClaimDetail | undefined = await this.claimStore.loadProviderClaimWithSuperFastClaimDetails(this.claimId);
        //     if(claimDetails) {
        //         this.setState(state => ({
        //             ...state,
        //             claim: claimDetails.claim,
        //         }));
        //     }
        // }
    };

    /**
     * Payers a pending HL7 payload names by id (subscribers and third-party administrators). A failure leaves them
     * unresolved — the overview then says "Payer unavailable" — rather than raising the error dialog over a page that
     * loaded. Demo mode never requests them (NS-14).
     */
    private loadHl7Payers = async (
        claimDetail: IClaimDetail | undefined,
        isStale: () => boolean,
    ): Promise<void> => {
        // Resolved from the response, not `this.state`: the setState that stores it may not have applied yet.
        const snapshot = this.resolvePendingHl7(claimDetail);
        if (!snapshot || this.props.rootStore.isDemo()) {
            return;
        }
        const ids = getHl7PayerIds(snapshot.payload);
        if (!ids.length) {
            return;
        }

        this.setState({ hl7PayersLoading: true });
        try {
            const payers = (await fetchPayersByIds(ids, 'provider')) as ProviderPayer[];
            if (!isStale()) {
                this.setState({ hl7Payers: payers });
            }
        } catch (err) {
            console.warn('Failed to load payers for the pending HL7 claim data', err);
        } finally {
            if (!isStale()) {
                this.setState({ hl7PayersLoading: false });
            }
        }
    };

    private fetchData = async (): Promise<void> => {
        if (!this.state.claim || !this.state.claimDetail) {
            // The claim this run was started for, and the run's own token. `this.claimId` is mutable
            // and changes under an in-flight request, so the id is read once here and every request
            // below is addressed with it — a response must never be filed against another claim.
            const requestedClaimId = this.claimId;
            const token = (this.fetchToken += 1);
            const isStale = (): boolean => token !== this.fetchToken;

            try {
                this.setState({ loading: true });

                this.fetchMessages(requestedClaimId);

                let claimDetail: IClaimDetail | undefined;

                if (this.state.legacy === 'true') {
                    // query for claim in case already available
                    let claim = this.claimStore.findClaim(requestedClaimId);
                    if (!claim) {
                        claim = await this.claimStore.loadClaim(requestedClaimId);
                    }
                    if (isStale()) {
                        return;
                    }
                    this.setState(state => ({
                        ...state,
                        claim,
                    }));
                    claimDetail = await this.fetchClaimDetails(requestedClaimId);
                    if (isStale()) {
                        return;
                    }
                    this.setState({
                        claimDetail,
                    });
                } else {
                    claimDetail = await this.fetchClaimDetails(requestedClaimId);
                    if (isStale()) {
                        return;
                    }
                    this.setState(state => ({
                        ...state,
                        claimDetail,
                        claim: claimDetail?.claim,
                    }));
                }
                // Not awaited: payer names fill in after the page renders instead of holding the page loader.
                void this.loadHl7Payers(claimDetail, isStale);
            } catch (err) {
                // A failure that belongs to a claim the user already left says nothing about the one
                // now on screen: reporting it would raise a dialog over the new claim, and
                // `err.claim_not_found` would redirect away from a claim that loaded fine.
                if (isStale()) {
                    return;
                }
                if (err && err.payload) {
                    const { payload } = err;
                    if (payload.code === 'err.application_access_token_has_expired') {
                        // NB: continue silently
                    } else if (payload.code === 'err.access_denied' || payload.code === 'err.claim_not_found') {
                        if (this.props.history) {
                            this.props.history.push(`/claims`);
                        }
                    } else {
                        this.dialogContext.showError(err);
                    }
                } else if (_.get(err, 'name') === 'AbortError' || _.get(err, 'name') === 'TypeError') {
                    // NB: continue silently
                    // AbortError is from cancelled requests;
                    // TypeError from CORS errors (failed to fetch);
                } else {
                    this.dialogContext.showError(err);
                }
            } finally {
                // Whoever superseded this run owns `loading` now; clearing it here would report the
                // newer claim as loaded while its own request is still out.
                if (!isStale()) {
                    this.setState({ loading: false });
                }
            }
        }
    };

    private readonly handleDismiss = (): void => {
        this.setState({
            showAlert: false,
        });
    };

    private readonly renderLoader = (): React.ReactElement => (
        <Fade in={true}>
            <S.Loader>
                <LoadingMeter />
            </S.Loader>
        </Fade>
    );
    private readonly renderPage = (): React.ReactElement => {
        const { showAlert, claimDetail, loading } = this.state;
        const isMissingClaim = claimDetail && !claimDetail?.professionalClaim && !claimDetail?.institutionalClaim;
        const pendingHl7 = this.pendingHl7Snapshot;
        const hasValidationErrors =
            claimDetail &&
            claimDetail.claim &&
            claimDetail.claim.claimValidated &&
            claimDetail.claim.claimValidationErrors &&
            claimDetail.claim.claimValidationErrors.length > 0;
        const { history } = this.props;

        const receiptIconVisible =
            this.state.claimDetail && this.state.claimDetail.patients && this.state.claimDetail.patients.length > 0;
        // Extract accountId from match params or fallback to extracting from URL pathname
        let accountId = this.props.match.params.accountId;
        if (!accountId) {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            accountId = pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        }
        // Use absolute paths since we're navigating between sibling routes
        const claimsPath = accountId ? `/${accountId}/claims` : '/claims';
        const breadcrumbLink =
            receiptIconVisible && accountId
                ? `/${accountId}/patients/${this.state.claimDetail.patients[0].id}/statement`
                : receiptIconVisible
                  ? `/patients/${this.state.claimDetail.patients[0].id}/statement`
                  : claimsPath;

        const handleEditClaimFromValidationErrorsBanner = (): void => {
            const editPath = accountId ? `/${accountId}/claims/${this.claimId}/edit` : `/claims/${this.claimId}/edit`;
            history?.push(editPath);
        };

        return (
            <PageLayout>
                {this.state.claim && <ClaimBanner claim={this.state.claim} />}
                {this.renderClaimHaltedBanner()}
                {hasValidationErrors && (
                    <ValidationErrorsBanner onEditClaim={handleEditClaimFromValidationErrorsBanner} />
                )}
                {isMissingClaim && !pendingHl7 && !hasValidationErrors && <MissingClaimBanner />}
                <S.StyledPageLayoutRow>
                    {((this.state.claim && (this.state.claim.patientFirst || this.state.claim.patientLast)) ||
                        (this.state.claim && this.state.claim.accountNumber) ||
                        (this.state.claimDetail &&
                            this.state.claimDetail.patients &&
                            this.state.claimDetail.patients.length > 0)) && (
                        <S.ClaimBreadcrumb>
                            <S.ClaimBreadcrumbClaimsLink to={claimsPath}>Claims</S.ClaimBreadcrumbClaimsLink>
                            {this.state.claim &&
                                (this.state.claim.patientFirst || this.state.claim.patientLast) &&
                                (() => {
                                    const baseName = _.compact([
                                        this.state.claim!.patientFirst,
                                        this.state.claim!.patientLast,
                                    ]).join(' ');
                                    const fullName = baseName
                                        ? _.compact([baseName, this.state.claim!.patientSuffix]).join(' ')
                                        : '';
                                    return (
                                        <S.ClaimBreadcrumbPatientLink to={breadcrumbLink}>
                                            {fullName}
                                            <S.BreadcrumbOpenIcon path={mdiOpenInNew} />
                                        </S.ClaimBreadcrumbPatientLink>
                                    );
                                })()}
                            {this.state.claim && this.state.claim.accountNumber && (
                                <S.ClaimNumber>
                                    <S.ClaimMetaLabel>(Claim: </S.ClaimMetaLabel>
                                    <S.ClaimMetaValue>{this.state.claim.accountNumber}</S.ClaimMetaValue>
                                    <S.ClaimMetaLabel>)</S.ClaimMetaLabel>
                                </S.ClaimNumber>
                            )}
                            {this.state.claimDetail &&
                                this.state.claimDetail.patients &&
                                this.state.claimDetail.patients.length > 0 && (
                                    <S.ClaimNumber>
                                        <S.ClaimMetaLabel>(Patient: </S.ClaimMetaLabel>
                                        <S.ClaimMetaValue>
                                            {this.state.claimDetail.patients[0].externalId}
                                        </S.ClaimMetaValue>
                                        <S.ClaimMetaLabel>)</S.ClaimMetaLabel>
                                    </S.ClaimNumber>
                                )}
                        </S.ClaimBreadcrumb>
                    )}
                    {this.renderMainHeader()}
                    {this.renderSubHeader()}
                    {this.renderClaimBundle()}
                </S.StyledPageLayoutRow>
                {pendingHl7 && (
                    <S.StyledPageLayoutRow>
                        <ClaimUpdatingNotice receivedAt={pendingHl7.receivedAt} />
                    </S.StyledPageLayoutRow>
                )}
                {showAlert && (
                    <S.StyledAlertBar
                        messages={this.openedMessages}
                        onDismiss={this.handleDismiss}
                        claimId={this.claimId}
                    />
                )}
                {!isMissingClaim && this.state.claim && (
                    <ClaimAiSummaryCard
                        claimId={this.claimId}
                        sources={this.buildClaimAiContextSources()}
                        utilityKey="claimDetails:Provider"
                        ready={!!this.state.claimDetail && this.templateLoaded}
                        professionalTemplate={this.professionalTemplate}
                        onAskFollowUp={this.openAiAssistant}
                    />
                )}
                {(!isMissingClaim || pendingHl7) && (
                    <S.StyledPageLayoutRow>
                        <ClaimDetailsPanel size="large">
                            {this.renderClaimOverview(true)}
                            {this.renderClaimFinancials()}
                            {this.renderRecordOfServices(true)}
                        </ClaimDetailsPanel>
                    </S.StyledPageLayoutRow>
                )}
                <S.StyledPageLayoutRow>{this.renderClaimProgressTracker()}</S.StyledPageLayoutRow>
                <S.StyledPageLayoutRow>{this.renderClaimTimelineTable()}</S.StyledPageLayoutRow>
                {/* {!isMissingClaim && <S.StyledPageLayoutRow>{this.renderBillingTimelineTable()}</S.StyledPageLayoutRow>} */}
            </PageLayout>
        );
    };
    private readonly renderMainHeader = (): React.ReactElement => {
        const { claim, claimDetail } = this.state;
        const accountId = this.props.match.params.accountId;
        const { history } = this.props;

        return (
            <S.MainHeader>
                <S.MainGroup>
                    <S.Title>{this.renderServiceDates()}</S.Title>
                    {this.renderResponsibilityBadge()}
                    {this.renderResponsibilityCategoryBadge()}
                    {this.renderCloseoutBadge()}
                    {this.renderMaxResponsibility()}
                    {this.state.claim && (
                        <ClaimCoderRuleSuppression
                            claimId={this.claimId}
                            api={coderRuleSuppressionApi}
                            codings={claimDetail?.codings}
                        />
                    )}
                </S.MainGroup>
                <S.Spacer />
                <ClaimMainHeaderActions
                    claimId={this.claimId}
                    claim={claim}
                    claimDetail={claimDetail}
                    messages={this.messages}
                    accountId={accountId}
                    history={history}
                    workflowHistory={this.props.rootStore.claimProgressStore.data}
                    aiOpen={this.state.aiOpen}
                    onAiOpenChange={open => this.setState({ aiOpen: open })}
                />
            </S.MainHeader>
        );
    };

    private isLoaded(keys: ClaimDetailsKey[]) {
        return keys.every(key => this.claimStore.claimDetailsLoader.get(key));
    }

    private readonly renderSubHeader = (): React.ReactElement | undefined => {
        const loaded = this.isLoaded(['providers', 'facilities', 'sellers']);

        if (this.state.claim && loaded) {
            return (
                <S.SubHeader>
                    {this.renderClaimTypeBadge()}
                    {this.renderProviders()}
                    {this.renderFacility()}
                    {this.renderPriorAuth()}
                    {this.renderSeller()}
                </S.SubHeader>
            );
        }
    };

    private readonly renderClaimBundle = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (claim?.claimBundleId && claim?.id) {
            return (
                <ClaimBundle
                    bundleId={claim.claimBundleId}
                    claimId={claim.id}
                    onLoadBundle={bundleId => this.claimStore.loadClaimBundle(bundleId)}
                />
            );
        }
    };

    private readonly renderServiceDates = (): string | undefined => {
        const { claim } = this.state;
        if (!claim) {
            return;
        }
        if (claim.fromDos && claim.throughDos && claim.fromDos !== claim.throughDos) {
            return `${formatDate(claim.fromDos, 'MMM d')} – ${formatDate(claim.throughDos, 'MMM d, yyyy')}`;
        } else if (claim.fromDos) {
            return formatDate(claim.fromDos, 'MMM d, yyyy');
        }
    };

    private readonly renderResponsibilityBadge = (): React.ReactElement | undefined => {
        if (this.state.claim) {
            return (
                <Tooltip
                    direction="top"
                    variant="dark"
                    title={
                        this.state.claim.claimResponsibilityType ? (
                            <span>{getResponsibilityDescription(this.state.claim.claimResponsibilityType)}</span>
                        ) : undefined
                    }>
                    <S.ResponsibilityStatus responsibility={this.state.claim.claimResponsibilityType} />
                </Tooltip>
            );
        }
    };

    private readonly renderResponsibilityCategoryBadge = (): React.ReactElement | undefined => {
        const category = this.state.claim?.claimResponsibilityCategoryType;
        if (!category) {
            return;
        }

        return (
            <S.ResponsibilityCategory>
                <ClaimResponsibilityCategoryBadge category={category} />
            </S.ResponsibilityCategory>
        );
    };

    private readonly renderCloseoutBadge = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (!claim) {
            return;
        }

        const closeoutValue = claim.closeoutVoid ? 'VOIDED' : claim.closeoutType;
        const closeoutLabelTitle = `Closeout: ${closeoutValue}`;

        const items: IActionMenuBadgeItem[] = [
            {
                label: 'Modify Closeout',
                action: async () => {
                    const currentClaim = this.state.claim;
                    if (!currentClaim) {
                        return;
                    }

                    const handleClose = (): void => {
                        this.dialogContext.hideDialog();
                    };

                    const handleSubmit = (updated: ProviderClaim): void => {
                        this.setState(state => ({
                            ...state,
                            claim: {
                                ...state.claim,
                                closeoutType: updated.closeoutType,
                                closeoutDescription: updated.closeoutDescription,
                                closeoutVoid: updated.closeoutVoid,
                            } as ProviderClaim,
                        }));
                        this.dialogContext.hideDialog();
                    };

                    this.dialogContext.showDialog({
                        props: {
                            onClose: handleClose,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: 'Modify Closeout',
                        },
                        Component: (
                            <ProviderCloseoutDialog
                                claim={currentClaim}
                                onCancel={handleClose}
                                onSubmit={handleSubmit}
                            />
                        ),
                    });
                },
            },
        ];

        return (
            <S.BadgeWrapper>
                <ActionMenuBadge
                    label={<BadgeLabeledValue prefix="Closeout:" value={closeoutValue} />}
                    labelTitle={closeoutLabelTitle}
                    items={items}
                    variant="metric"
                />
            </S.BadgeWrapper>
        );
    };
    /**
     * Asks which payer subscriber / patient the CMS-1500 is for, then resolves with that form's URL.
     * Resolves `null` when the user dismisses the dialog, so callers can tell "cancelled" from "failed".
     */
    private readonly selectCms1500 = (options: ICms1500Choice): Promise<{ url?: string } | null> =>
        new Promise((resolve, reject) => {
            showCms1500Dialog(this.dialogContext, {
                payerSubscribers: options.payerSubscribers,
                subscriberPatients: options.subscriberPatients,
                defaultPayerSubscriberId: options.defaultPayerSubscriberId,
                defaultSubscriberPatientId: options.defaultSubscriberPatientId,
                onCancel: () => resolve(null),
                onConfirm: selection => {
                    ClaimService.viewCMS1500({ id: options.claimPindId, ...selection }).then(resolve, reject);
                },
            });
        });

    private readonly renderClaimTypeBadge = (): React.ReactElement | undefined => {
        const { claim } = this.state;
        if (claim && claim.type) {
            let claimType = '';
            if (claim.type === ClaimType.INSTITUTIONAL) {
                claimType = ' CMS-1450 / UB04';
            } else if (claim.type === ClaimType.PROFESSIONAL) {
                claimType = ' CMS-1500';
            } else if (claim.type === ClaimType.NYNF3) {
                claimType = 'NF-3';
            }

            const label = `${claimType || ''} Claim ID – ${truncateUuid(this.claimId)}`;
            const openCMS1500 = (selection: ICms1500Selection) => {
                const claimPindId = this.claimPIND?.id;
                if (!claimPindId) {
                    this.dialogContext.showError(new Error('Can`t fetch the document'));
                    return;
                }

                // Opened synchronously from the click so the popup is not blocked, then pointed at the form.
                const popup = window.open('', '_blank');
                ClaimService.viewCMS1500({ id: claimPindId, ...selection })
                    .then(documentUrl => {
                        if (popup && documentUrl?.url) {
                            popup.location.href = documentUrl.url;
                        } else {
                            popup?.close();
                        }
                    })
                    .catch(err => {
                        popup?.close();
                        this.dialogContext.showError(err);
                    });
            };
            // The user picks the payer subscriber and patient (and flat vs unflattened) before the form is built.
            const items: IActionMenuBadgeItem[] =
                claim.type === ClaimType.PROFESSIONAL && this.claimPIND?.id && this.payerSubscribers?.length
                    ? [
                          {
                              label: 'View CMS-1500…',
                              action: async () =>
                                  showCms1500Dialog(this.dialogContext, {
                                      payerSubscribers: this.payerSubscribers || [],
                                      subscriberPatients: this.subscriberPatients || [],
                                      onConfirm: openCMS1500,
                                  }),
                          },
                      ]
                    : [];

            const accountId = this.props.match.params.accountId;

            return (
                <ActionMenuBadge
                    label={label}
                    items={items}
                    id={this.claimId}
                    path={accountId ? `/${accountId}/claims/${this.claimId}` : `/claims/${this.claimId}`}
                    Symbol={<BadgeIcon path={mdiClipboardTextOutline} />}
                />
            );
        }
    };
    /**
     * Open one provider's detail.
     *
     * Reached from the chip's chevron menu rather than by replacing the chip's click.
     * `ActionMenuBadge`'s `clickAction` REPLACES the menu toggle, so wiring it there would
     * take `Copy ID` away — and the chevron the design draws on these chips is that menu.
     */
    private readonly showProviderDetail = (provider: ProviderProvider) => {
        this.dialogContext.showDialog({
            props: {
                onClose: () => this.dialogContext.hideDialog(),
                renderTitle: getProviderTypeLabel(provider.type),
                showCloseButton: true,
                maxWidth: 'sm',
                fullWidth: true,
            },
            Component: <ProviderDetail provider={provider} />,
        });
    };

    private readonly renderProviders = () => {
        if (this.providers?.length) {
            return this.providers.map(provider => (
                <ProviderBadge
                    key={provider.id}
                    id={provider.id}
                    first={provider.first}
                    suffix={provider.suffix}
                    last={provider.last}
                    type={provider.type}
                    organization={provider.organization}
                    npi={provider.npi}
                    items={[{ label: 'View details', action: () => this.showProviderDetail(provider) }]}
                />
            ));
        }

        return <MissingIdBadge>No provider(s)</MissingIdBadge>;
    };
    private readonly renderFacility = () => {
        if (this.facility) {
            return <FacilityBadge id={this.facility.id} name={this.facility.name} npi={this.facility.npi} />;
        } else {
            return <MissingIdBadge>No facility</MissingIdBadge>;
        }
    };

    private readonly renderPriorAuth = (): React.ReactElement[] | undefined => {
        const auths = getClaimPriorAuthorizations(
            this.professionalTemplate || this.institutionalTemplate,
        );
        if (!auths.length) {
            return undefined;
        }

        return auths.map(auth => (
            <PriorAuthBadge key={auth.label} label={auth.label} number={auth.number} />
        ));
    };

    private readonly renderSeller = (): React.ReactElement | undefined => {
        if (this.seller && this.seller.id && this.seller.first && this.seller.last) {
            const { id, first, last } = this.seller;
            return <SellerBadge id={id} first={first} last={last} />;
        }
    };

    private readonly renderMaxResponsibility = (): React.ReactElement | undefined => {
        if (this.state.claim && this.state.claim.id) {
            const maxPrValue =
                this.state.claim.maximumGuarantorResponsibility &&
                this.state.claim.maximumGuarantorResponsibility.amount
                    ? formatAmount(this.state.claim.maximumGuarantorResponsibility.amount)
                    : 'None';
            const maxPrLabelTitle = `Max PR: ${maxPrValue}`;

            const items: IActionMenuBadgeItem[] = [
                {
                    label: 'Modify Max PR',
                    action: async () => {
                        const handleClose = (): void => {
                            this.dialogContext.hideDialog();
                        };

                        const handleSubmit = (): void => {
                            this.dialogContext.hideDialog();
                            window.location.reload(); //For now, just reload the screen
                        };

                        this.dialogContext.showDialog({
                            props: {
                                onClose: handleClose,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Modify Maximum Patient Responsibility',
                            },
                            Component: (
                                <MaxProviderPRDialog
                                    maximumGuarantorResponsibility={this.state.claim?.maximumGuarantorResponsibility}
                                    claimId={this.state.claim && this.state.claim.id ? this.state.claim.id : ''}
                                    onClose={handleClose}
                                    onSubmit={handleSubmit}
                                />
                            ),
                        });
                    },
                },
            ];

            return (
                <ActionMenuBadge
                    label={<BadgeLabeledValue prefix="Max PR:" value={maxPrValue} />}
                    labelTitle={maxPrLabelTitle}
                    items={items}
                    Symbol={<BadgeIcon path={mdiAccountOutline} />}
                    variant="metric"
                />
            );
        }
    };
    private readonly renderClaimOverview = (embedded = false): React.ReactElement => {
        const loaded = this.isLoaded([
            'guarantors',
            'subscriberPatients',
            'payerSubscribers',
            'payers',
            'subscribers',
            'facilities',
        ]);

        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const accountId = pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        const pendingHl7 = this.pendingHl7Snapshot;
        const hl7Overview = pendingHl7 ? buildHl7ClaimOverview(pendingHl7.payload, this.state.hl7Payers) : undefined;
        const subscriberPatient = hl7Overview ? hl7Overview.subscriberPatient : _.first(this.subscriberPatients);
        const patient = subscriberPatient?.patient;
        const viewFullDetailsHref =
            patient?.id != null
                ? accountId
                    ? `/${accountId}/patients/${patient.id}/statement`
                    : `/patients/${patient.id}/statement`
                : undefined;
        const patientDetailsHref = patient?.externalId
            ? accountId
                ? `/${accountId}/patients/details/${patient.externalId}`
                : `/patients/details/${patient.externalId}`
            : undefined;

        if (hl7Overview) {
            // Everything here is in the payload itself; only payer names arrive later, without holding the card.
            return (
                <ProviderClaimOverview
                    embedded={embedded}
                    loading={false}
                    payersLoading={this.state.hl7PayersLoading}
                    guarantor={hl7Overview.guarantor}
                    subscriberPatient={subscriberPatient}
                    payerSubscribers={hl7Overview.payerSubscribers}
                    payers={hl7Overview.payers}
                    subscribers={hl7Overview.subscribers}
                    thirdPartyAdministrators={hl7Overview.thirdPartyAdministrators}
                    viewFullDetailsHref={viewFullDetailsHref}
                    patientDetailsHref={patientDetailsHref}
                    facility={hl7Overview.facility}
                />
            );
        }

        const thirdPartyAdministrators = [
            this.state.claimDetail?.professionalClaim?.primaryThirdPartyAdministratorPayer,
            this.state.claimDetail?.professionalClaim?.secondaryThirdPartyAdministratorPayer,
            this.state.claimDetail?.professionalClaim?.tertiaryThirdPartyAdministratorPayer,
        ];

        return (
            <ProviderClaimOverview
                embedded={embedded}
                loading={!loaded || !this.templateLoaded}
                guarantor={this.guarantor}
                subscriberPatient={subscriberPatient}
                payerSubscribers={this.payerSubscribers}
                payers={this.payers}
                subscribers={this.subscribers}
                thirdPartyAdministrators={thirdPartyAdministrators}
                viewFullDetailsHref={viewFullDetailsHref}
                patientDetailsHref={patientDetailsHref}
                facility={this.facility}
            />
        );
    };
    private readonly renderPersonalOverview = (): React.ReactElement | undefined => {
        const primaryPayerSubscriber = this.payerSubscribers && this.payerSubscribers[0];
        const loaded = this.isLoaded(['guarantors', 'subscriberPatients', 'payerSubscribers', 'invoices']);

        const patientInvoice = _.find(this.invoices, { invoiceType: InvoiceType.PATIENT });

        return (
            <ClaimPersonalOverview
                patientInvoice={patientInvoice}
                claimId={this.claimId}
                loading={!loaded || !this.templateLoaded}
                guarantor={this.guarantor}
                subscriberPatient={_.first(this.subscriberPatients)}
                payerSubscriber={primaryPayerSubscriber}
                institutionalTemplate={this.institutionalTemplate}
                collections={this.collections}
            />
        );
    };
    private readonly renderInsuranceOverview = (): React.ReactElement | undefined => {
        const loaded = this.isLoaded([
            'payers',
            'payerSubscribers',
            'subscribers',
            'subscriberPatients',
            'eligibilities',
        ]);

        const thirdPartyAdministrators = [
            this.state.claimDetail?.professionalClaim?.primaryThirdPartyAdministratorPayer,
            this.state.claimDetail?.professionalClaim?.secondaryThirdPartyAdministratorPayer,
            this.state.claimDetail?.professionalClaim?.tertiaryThirdPartyAdministratorPayer,
        ];

        return (
            <InsuranceOverview
                loading={!loaded}
                payers={this.payers}
                payerSubscribers={this.payerSubscribers}
                subscribers={this.subscribers}
                subscriberPatients={this.subscriberPatients}
                thirdPartyAdministrators={thirdPartyAdministrators}
                eligibilities={this.eligibilities}
            />
        );
    };
    private readonly renderFinancialOverview = (): React.ReactElement | undefined => {
        // calculate goal by adding up accepted amounts from ChargemasterItems which match service line items
        // let goal = 0;
        // if (this.template) {
        //     const serviceLines: { chargemasterItemId?: string }[] = _.concat(
        //         [],
        //         _.get(this.template, 'f15ServiceLines'),
        //         _.get(this.template, 'f24ServiceLine'),
        //         _.get(this.template, 'f42ClaimLines'),
        //     );
        //     goal = _.reduce(
        //         serviceLines,
        //         (r, line) => {
        //             const item = _.find(this.chargemasterItems, { id: line?.chargemasterItemId });
        //             return r + parseAmount(item?.expected);
        //         },
        //         0,
        //     );
        // }

        const goal = parseAmount(this.state.claim?.chargemasterAllowedAmount);

        const allowed = parseAmount(this.state.claim?.allowedAmount);

        const totalCharges = parseAmount(this.state.claim?.chargeAmount);

        const payerPaid = parseAmount(this.state.claim?.payerAmount);

        const patientPaid = parseAmount(this.state.claim?.patientInvoicePaidAmount);

        const patientAmount = parseAmount(this.state.claim?.patientInvoiceAmount);

        let remaining = 0;

        // a patient remaining balance should not be calculated on the UI when adjudication has not completed. When calculating the patient remaining amount, the collection status needs to be taken into account to determine if the claim has been fully adjudicated before we alert to the provider that a refund is required. This can be done by having the patient remaining amount due be $0 if the collection status is null, NOT_STARTED , SECONDARY_BILL , or TERTIARY_BILL . In this case, the collection status is NOT_STARTED .
        // https://app.clickup.com/t/2pc0bh6
        if (
            this.state.claim?.collectionStatus &&
            ![
                ProviderCollectionStatusType.NOTSTARTED,
                ProviderCollectionStatusType.SECONDARYBILL,
                ProviderCollectionStatusType.TERTIARYBILL,
            ].includes(this.state.claim.collectionStatus)
        ) {
            remaining = this.state.claim?.claimAdjudicationDate
                ? patientAmount - patientPaid
                : Math.max(0, patientAmount - patientPaid);
        }

        const financial = {
            total: {
                name: 'Charge',
                amount: totalCharges,
            },
            segments: [
                {
                    name: 'Payer Paid',
                    amount: payerPaid,
                    infoMessage: this.state.claim?.claimAdjudicationDate
                        ? `Adjudication: ${formatDate(this.state.claim.claimAdjudicationDate, 'MMM dd, yyyy hh:mm')}`
                        : `Adjudication: N/A`,
                },
                { name: 'Patient Paid', amount: patientPaid },
                {
                    shortLabel: 'Remaining',
                    name: 'Patient Remaining',
                    amount: remaining,
                    showRefundWarning: true,
                },
            ],
            goal: {
                name: 'Accepted',
                amount: goal,
            },
        };
        return undefined;
    };

    private readonly renderRecordOfServices = (embedded = false): React.ReactElement => {
        const loaded = this.isLoaded(['providers', 'chargemasterItems', 'payments']);
        const pendingHl7 = this.pendingHl7Snapshot;
        // Lines stay on the last processed claim while newer data is pending. With no processed claim yet there are
        // no lines to wait for, so the table is empty rather than loading forever.
        const noProcessedClaim = Boolean(pendingHl7 && !this.state.claimDetail?.professionalClaim);
        return (
            <RecordOfServices
                embedded={embedded}
                updating={Boolean(pendingHl7)}
                loading={!noProcessedClaim && (!loaded || !this.templateLoaded)}
                template={this.template}
                providers={this.providers}
                chargemasterItems={this.chargemasterItems}
                payments={this.payments}
                claimPatientPaid={this.state.claim?.patientInvoicePaidAmount}
                payerSubscribers={this.payerSubscribers}
            />
        );
    };

    private readonly renderClaimFinancials = (): React.ReactElement => {
        const loaded = this.isLoaded(['providers', 'chargemasterItems', 'payments']);
        const financialsLoading = !loaded || !this.templateLoaded;
        const financials = financialsLoading ? undefined : toClaimFinancialSummary(this.calculateClaimFinancials());

        return (
            <ClaimFinancialsSection
                loading={financialsLoading}
                financials={financials}
                updating={Boolean(this.pendingHl7Snapshot)}
            />
        );
    };

    private readonly renderClaimProgressTracker = (): React.ReactElement => {
        // The claim's own closeout completes Closure (the history has no "closed" status); undefined until known.
        const claim = this.state.claim;
        const closeout = claim?.closeoutType
            ? {
                  closed: claim.closeoutType === ClaimCloseoutType.CLOSED,
                  voided: Boolean(claim.closeoutVoid),
                  description: claim.closeoutDescription ? normalizeStr(claim.closeoutDescription) : undefined,
              }
            : undefined;
        // Unknown until the payer subscribers have loaded; this model has no per-subscriber `active` flag (NS-3).
        const hasAdditionalCoverage = resolveHasAdditionalCoverage(
            this.isLoaded(['payerSubscribers']),
            this.payerSubscribers,
        );

        return (
            <ClaimProgressTracker
                claimId={this.claimId}
                claimProgressStore={this.props.rootStore.claimProgressStore}
                hasAdditionalCoverage={hasAdditionalCoverage}
                closeout={closeout}
            />
        );
    };

    private readonly renderClaimPayerSummary = (): React.ReactElement => {
        const loaded = this.isLoaded(['providers', 'chargemasterItems', 'payments']);
        //Bug fix: claim payer summary is inaccurate if we have old professional payments, so hide it, if so
        let hasOldProfessionalPayments = false;
        if (loaded) {
            hasOldProfessionalPayments = this.claimStore.hasOldProfessionalPayments(this.claimId);
        }
        if (hasOldProfessionalPayments) {
            return <></>;
        } else {
            return (
                <ClaimPayerSummary
                    loading={!loaded || !this.templateLoaded}
                    template={this.template}
                    chargemasterItems={this.chargemasterItems}
                    payments={this.payments}
                    providers={this.providers}
                />
            );
        }
    };

    private readonly renderClaimTimelineTable = (): React.ReactElement => {
        const resources = aggregateClaimResources({
            claim: this.state.claim,
            claimPind: this.claimPIND,
            codings: this.codings,
            workerFunctions: this.state.claimDetail?.workerFunctions,
            collections: this.collections,
            collectionInvoiceEvents: this.state.claimDetail?.collectionInvoiceEvents,
            comments: this.comments,
            documents: this.documents,
            statuses: this.statuses,
            eligibilities: this.eligibilities,
            hl7Messages: this.hl7Messages,
            invoices: this.invoices,
            letters: this.letters,
            messages: this.messages,
            mails: this.mails,
            payments: this.payments,
            phones: this.phones,
            receiveFaxes: this.receiveFaxes,
            sendFaxes: this.sendFaxes,
            sendEmails: this.emails,
            transmissions: this.transmissions,
            transactions: this.transactions,
        });
        const messageLinkedCodingIds = new Set(
            _.compact(
                _.flatMap(resources, resource => {
                    if (resource.resourceType !== 'MESSAGE') {
                        return [];
                    }

                    return _.map((resource as any).links?.codings || [], codingLink => codingLink.id);
                }),
            ),
        );

        // Build context for item mapper
        const itemContext = {
            claim: this.state.claim,
            claimPind: this.claimPIND,
            template: this.template,
            payerSubscribers: this.payerSubscribers,
            selectCms1500: this.selectCms1500,
            letters: this.letters,
            mails: this.mails,
            receiveFaxes: this.receiveFaxes,
            sendFaxes: this.sendFaxes,
            payments: this.payments,
            transactions: this.transactions,
            paymentPlans: this.paymentPlans,
            subscriberPatients: this.subscriberPatients,
            messageStore: this.messageStore,
            onOpenMessage: this.handleOpenMessage,
            onSetDiscount: this.handleSetDiscount,
            claimStore: this.claimStore,
            rootStore: this.props.rootStore,
            resources,
        };

        return (
            <ClaimTimeline
                appType="provider"
                loading={this.state.loading}
                resources={resources}
                claim={this.state.claim}
                claimBundle={this.state.claimBundle}
                loadingBundle={this.state.loadingBundle}
                onLoadClaimBundle={this.onLoadClaimBundle}
                getItem={resource => {
                    const item = getProviderItem(resource, itemContext);
                    if (resource.resourceType !== 'CODING') {
                        return item;
                    }

                    return {
                        ...item,
                        codingSignature: getCodingOutcomeSignature(resource as any),
                        isMessageLinkedCoding: messageLinkedCodingIds.has(resource.id || ''),
                    };
                }}
                dialogContext={this.dialogContext}
                messagingContext={this.props.messagingContext}
                messageStore={this.messageStore}
                showTitleCard={true}
            />
        );
    };

    private readonly handleOpenMessage = (message: any) => {
        // Only pass messageId - always fetch fresh data from API
        this.props.messagingContext.openPanel?.({
            claimId: this.state.claim?.id,
            messageId: message.id,
        });
    };

    private readonly handleSetDiscount = (invoice: Invoice) => {
        const claim = this.state.claim;
        if (!claim?.id) {
            return;
        }

        showRecordDiscountDialog({
            dialogContext: this.dialogContext,
            toastContext: this.props.toastContext,
            claimStore: this.claimStore,
            claimId: claim.id,
            fromDos: claim.fromDos,
            invoice,
        });
    };

    private readonly onLoadClaimBundle = async (bundleId: string) => {
        this.setState({ loadingBundle: true });

        try {
            const claimBundle = await this.claimStore.loadClaimBundle(bundleId);

            this.setState({ claimBundle });
        } finally {
            this.setState({ loadingBundle: false });
        }
    };

    private readonly renderBillingTimelineTable = (): React.ReactElement => {
        const loaded = this.isLoaded([
            'checkoutCharges',
            'creditCards',
            'guarantors',
            'invoices',
            'paymentPlans',
            'transactions',
            'payments',
        ]);

        return (
            <BillingTimelineTable
                loading={!loaded}
                checkoutCharges={this.checkoutCharges}
                claimId={this.claimId}
                creditCards={this.creditCards}
                guarantor={this.guarantor}
                invoices={this.invoices}
                paymentPlans={this.paymentPlans}
                transactions={this.transactions}
                payments={this.payments}
                Header={this.renderHeader()}
            />
        );
    };

    private readonly renderHeader = (): React.ReactElement => {
        const patientInvoice = _.find(this.invoices, { invoiceType: InvoiceType.PATIENT });
        return <GuarantorHeader claimId={this.claimId} guarantor={this.guarantor} patientInvoice={patientInvoice} />;
    };
}

interface IClaimMainHeaderActionsProps {
    claimId: string;
    claim: ProviderClaim | undefined;
    claimDetail: IClaimDetail | undefined;
    messages?: ProviderMessage[];
    accountId?: string;
    history?: RouterProps<IMatchParams>['history'];
    workflowHistory?: unknown[];
    aiOpen: boolean;
    onAiOpenChange: (open: boolean) => void;
}

const ClaimMainHeaderActions: React.FC<IClaimMainHeaderActionsProps> = ({
    claimId,
    claim,
    claimDetail,
    messages,
    accountId,
    history,
    workflowHistory,
    aiOpen,
    onAiOpenChange,
}) => {
    const {
        handleClick: handleTalkToAiClick,
        checking: isAiChecking,
        tooltipTitle: aiTooltipTitle,
        showExtensionInstructions,
        closeExtensionInstructions,
        openExtensionStore,
        retryConnection,
    } = useTalkToAiTrigger(() => onAiOpenChange(true));

    const handleEditClick = () => {
        const editPath = accountId ? `/${accountId}/claims/${claimId}/edit` : `/claims/${claimId}/edit`;
        history?.push(editPath);
    };

    const claimContextSources = React.useMemo(
        () => ({
            claim: claimDetail?.claim ?? claim,
            professionals: claimDetail?.professionalClaim ? [claimDetail.professionalClaim] : undefined,
            hl7s: claimDetail?.hl7Messages,
            eligibilities: claimDetail?.eligibilities,
            codings: claimDetail?.codings,
            messages,
            payments: claimDetail?.payments,
            collectionPayments: claimDetail?.collections,
            transmissions: claimDetail?.transmissions,
            invoices: claimDetail?.invoices,
            documents: claimDetail?.documents,
            letters: claimDetail?.letters,
            receiveFaxes: claimDetail?.receiveFaxes,
            sendFaxes: claimDetail?.sendFaxes,
            history: workflowHistory,
        }),
        [claim, claimDetail, messages, workflowHistory],
    );

    const attachableEntities = React.useMemo(
        () => buildClaimAttachableEntities(claimContextSources),
        [claimContextSources],
    );

    const resolveContextData = React.useCallback(
        (question: string, attachedItems: AttachedItem[], matchedKeys: string[]) =>
            buildClaimSmartContext({
                question,
                sources: claimContextSources,
                attachedItems,
                previouslyMatchedKeys: matchedKeys,
            }),
        [claimContextSources],
    );

    return (
        <S.ActionButtons>
            <S.EditButton variant="primary" size="medium" onClick={handleEditClick}>
                Edit claim
            </S.EditButton>
            <NewMessageButton claimId={claimId} />
            <S.AskButtonWrapper>
                <Tooltip direction="top" variant="dark" title={<span>{aiTooltipTitle}</span>}>
                    <S.CustomAIButton onClick={handleTalkToAiClick} disabled={isAiChecking}>
                        <S.TalkToClaimIcon path={mdiCreationOutline} size="16px" />
                        Talk to Claim
                    </S.CustomAIButton>
                </Tooltip>
            </S.AskButtonWrapper>
            {claim && (
                <AiAssistant
                    open={aiOpen}
                    onClose={() => onAiOpenChange(false)}
                    conversationId={`claim:${claim.id ?? claimId}`}
                    entity="Claim"
                    subjectLabel="claim"
                    suggestionsKey="default"
                    resolveContextData={resolveContextData}
                    attachableEntities={attachableEntities}
                    utilityKey="claimDetails:Provider"
                />
            )}
            <ExtensionUnavailableModal
                open={showExtensionInstructions}
                onClose={closeExtensionInstructions}
                onGoToStore={openExtensionStore}
                onRetry={retryConnection}
            />
        </S.ActionButtons>
    );
};

const ClaimBanner: React.FC<{ claim: ProviderClaim }> = ({ claim }) => {
    const messagingContext = useContext(ProviderMessagingContext);

    const handleCreateTask = (): void => {
        if (!claim?.id) return;

        messagingContext.openPanel?.({
            claimId: claim.id,
            isComposeOpen: true,
            defaultSubject: 'Claim needs Human Intervention',
            defaultBody: 'This claim must be flagged as needing human intervention. Please review.',
        });
    };

    if (claim.collectionStatus === ProviderCollectionStatusType.HUMANINTERVENTIONNEEDED) {
        return (
            <S.Alert>
                <S.HaltedAlertContainer>
                    <Icon path={mdiAccountOutline} size={1} />
                    <S.AlertTitle>Human Intervention Needed</S.AlertTitle>
                </S.HaltedAlertContainer>
                <S.AlertButton variant="primary" size="small" onClick={handleCreateTask}>
                    Create task
                </S.AlertButton>
            </S.Alert>
        );
    }

    if (claim?.closeoutType === ClaimCloseoutType.CLOSED) {
        return (
            <S.Alert type="success">
                <S.HaltedAlertContainer>
                    <Icon path={mdiCheck} size={1} />
                    <S.AlertTitle>
                        {claim.closeoutVoid ? 'This claim is closed and voided.' : 'This claim is closed.'}
                    </S.AlertTitle>
                    {claim.closeoutVoid && (
                        <S.AlertDescription>Voided claims cannot be reopened.</S.AlertDescription>
                    )}
                    {claim.closeoutDescription && (
                        <S.AlertDescription>
                            {renderString(claim.closeoutDescription, {
                                type: 'String',
                                normalized: true,
                            })}
                        </S.AlertDescription>
                    )}
                </S.HaltedAlertContainer>
            </S.Alert>
        );
    }

    return null;
};

const ValidationErrorsBanner: React.FC<{ onEditClaim?: () => void }> = ({ onEditClaim }) => {
    return (
        <S.Alert>
            <S.HaltedAlertContainer>
                <Icon path={mdiPencilOutline} size={1} />
                <div>
                    <S.AlertTitle>
                        Claim has validation errors that have prevented it from being automatically generated.
                    </S.AlertTitle>
                </div>
            </S.HaltedAlertContainer>
            {onEditClaim && (
                <S.AlertButton variant="primary" size="small" onClick={onEditClaim}>
                    Edit claim
                </S.AlertButton>
            )}
        </S.Alert>
    );
};

const MissingClaimBanner: React.FC = () => {
    return (
        <S.Alert>
            <S.HaltedAlertContainer>
                <Icon path={mdiAlertCircle} size={1} />
                <S.AlertTitle>
                    Claim details are not available because the claim is currently in progress of getting created.
                </S.AlertTitle>
            </S.HaltedAlertContainer>
        </S.Alert>
    );
};

const NewMessageButton = ({ claimId }: { claimId: string }) => {
    const messagingContext = useContext(ProviderMessagingContext);
    const handleNewMessage = () => {
        messagingContext.openPanel?.({
            claimId,
            isComposeOpen: true,
        });
    };

    return (
        <Tooltip direction="top" size="large" variant="dark" title={<span>Send a message about this claim</span>}>
            <S.ComposeButton variant="outlined" size="medium" onClick={handleNewMessage}>
                <S.ComposeIcon path={mdiPlus} size="16px" />
                <S.ComposeLabel>New message</S.ComposeLabel>
            </S.ComposeButton>
        </Tooltip>
    );
};

const ClaimDetailPageWithRouter = withRouter(ClaimDetailPage);

export const ClaimDetail: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore): React.ReactElement => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        const messagingContext = useContext(ProviderMessagingContext);

        return (
            <ClaimDetailPageWithRouter
                authContext={authContext}
                dialogContext={dialogContext}
                toastContext={toastContext}
                messagingContext={messagingContext}
                rootStore={rootStore}
            />
        );
    }),
);

const ProviderClaimOverview: React.FC<{
    loading: boolean;
    embedded?: boolean;
    guarantor: ProviderGuarantor | undefined;
    facility: ProviderFacility | undefined;
    subscriberPatient: ProviderSubscriberPatient | undefined;
    payerSubscribers: ProviderPayerSubscriber[] | undefined;
    payers: ProviderPayer[] | undefined;
    subscribers: ProviderSubscriber[] | undefined;
    thirdPartyAdministrators: Array<ProviderPayer | undefined>;
    viewFullDetailsHref?: string;
    patientDetailsHref?: string;
    payersLoading?: boolean;
}> = ({
    loading,
    payersLoading,
    embedded,
    guarantor,
    facility,
    subscriberPatient,
    payerSubscribers,
    payers,
    subscribers,
    thirdPartyAdministrators,
    viewFullDetailsHref,
    patientDetailsHref,
}) => (
    <ClaimOverview
        embedded={embedded}
        loading={loading}
        payersLoading={payersLoading}
        guarantor={guarantor}
        facility={facility}
        subscriberPatient={subscriberPatient}
        payerSubscribers={payerSubscribers}
        payers={payers}
        subscribers={subscribers}
        thirdPartyAdministrators={thirdPartyAdministrators}
        patientDetailsHref={patientDetailsHref}
        viewFullDetailsHref={viewFullDetailsHref}
    />
);

export const ClaimPersonalOverview: React.FC<{
    claimId: string;
    loading: boolean;
    patientInvoice: Invoice | undefined;
    guarantor: ProviderGuarantor | undefined;
    subscriberPatient: ProviderSubscriberPatient | undefined;
    payerSubscriber: ProviderPayerSubscriber | undefined;
    institutionalTemplate: ProviderInstitutionalClaimTemplate | undefined;
    collections: ProviderCollectionPayment[] | undefined;
}> = inject('rootStore')(
    observer(props => {
        const {
            claimId,
            loading,
            guarantor,
            subscriberPatient,
            payerSubscriber,
            institutionalTemplate,
            patientInvoice,
            collections,
        } = props;
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        const rootStore: RootStore = _.get(props, 'rootStore');
        if (!rootStore) {
            return <></>;
        }

        // Extract accountId from URL
        const getAccountId = (): string | undefined => {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
        };
        const accountId = getAccountId();

        const { claimStore } = rootStore;

        const handleGuarantorLink = async () => {
            try {
                if (guarantor && guarantor.id) {
                    const tempUrl = await claimStore.getGuarantorViewBillUrl(guarantor.id);
                    if (tempUrl && tempUrl.url) {
                        window.open(tempUrl.url, '_blank');
                        return;
                    }
                }
            } catch (err) {
                dialogContext.showError(err);
            }
            toastContext.notify?.('Billing document is currently unavailable.', 'right');
        };

        const handleCloseDialog = () => {
            dialogContext.hideDialog();
        };

        const handleGuarantorCheckout = async () => {
            if (!guarantor?.id) {
                return;
            }

            try {
                const shortUrl = await claimStore.getGuarantorCheckoutUrl(guarantor.id);
                if (shortUrl && shortUrl.url) {
                    window.open(shortUrl.url, '_blank');
                }
            } catch (err) {
                const parsedError = await parseError(err);
                if (parsedError.payload.code === 'err.billing_not_active_for_invoice') {
                    dialogContext.showDialog({
                        props: {
                            onClose: handleCloseDialog,
                            disableBackdropClick: true,
                            disableEscapeKeyDown: true,
                            renderTitle: 'Billing is not active for this invoice yet',
                        },
                        Component: (
                            <ErrorDialog
                                onClose={handleCloseDialog}
                                payload={{
                                    message: 'If you feel this is in error, please reach out to support.',
                                    hideTrace: true,
                                }}
                            />
                        ),
                    });
                } else {
                    dialogContext.showError(err);
                }
            }
        };

        const handleRefund = () => {
            dialogContext.showDialog({
                props: {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Refund Payment',
                },
                Component: (
                    <CollectionRefundDialog
                        onClose={handleCloseDialog}
                        onSubmit={handleCloseDialog}
                        collectionPayments={
                            collections
                                ? collections.filter(c => c.source !== CollectionPaymentSourceType.TERMINAL)
                                : []
                        }
                    />
                ),
            });
        };

        const handleRecordPayment = (): void => {
            dialogContext.showDialog({
                props: {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Record Payment',
                },
                Component: <CollectionPaymentDialog onClose={handleCloseDialog} claimId={claimId} />,
            });
        };

        const handleDiscount = () => {
            if (patientInvoice) {
                showRecordDiscountDialog({ dialogContext, toastContext, claimStore, claimId, invoice: patientInvoice });
            }
        };

        // <S.GuarantorAmount>
        //     {patientInvoice && (
        //         <>
        //             {renderAmount(patientInvoice.paidAmount)} / {renderAmount(patientInvoice.amount)}
        //         </>
        //     )}
        // </S.GuarantorAmount>

        return (
            <PersonalOverview
                loading={loading}
                guarantor={guarantor}
                subscriberPatient={subscriberPatient}
                payerSubscriber={payerSubscriber}
                institutionalTemplate={institutionalTemplate}
                onGuarantorCheckout={handleGuarantorCheckout}
                onGuarantorLink={handleGuarantorLink}
                patientInvoice={patientInvoice}
                accountId={accountId}
                guarantorActions={_.compact([
                    // {
                    //     onClick: handleRefund,
                    //     icon: <Emoji type="repeat" title="Refund" />,
                    //     label: 'Record Refund',
                    // },
                    // {
                    //     onClick: handleRecordPayment,
                    //     icon: <Emoji type="writingHand" title="Record Payment" />,
                    //     label: 'Record Payment',
                    // },
                    // patientInvoice
                    //     ? {
                    //           onClick: handleDiscount,
                    //           icon: <Emoji type="moneyWings" title="Set Discount" />,
                    //           label: 'Set Discount',
                    //       }
                    //     : undefined,
                ])}
            />
        );
    }),
);
